import React, { useState } from 'react';
import { X, Upload } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useFirebaseAuth } from "../context/FirebaseAuthContext";
import { db, storage } from "../lib/firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const WorkExperienceModal = ({ onClose, onSuccess, editMode = false, experienceData = null }) => {
    const { user } = useFirebaseAuth();
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formData, setFormData] = useState({
        title: editMode ? experienceData?.title || '' : '',
        description: editMode ? experienceData?.description || '' : ''
    });
    const [file, setFile] = useState(null);
    const [existingFile, setExistingFile] = useState(editMode ? experienceData?.original_filename : null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user) {
            toast.error('You must be logged in to add work experience');
            return;
        }

        setLoading(true);
        setUploadProgress(0);

        try {
            let fileUrl = null;
            let fileName = null;

            // Upload file if provided
            if (file) {
                const timestamp = Date.now();
                const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const filePath = `work-experiences/${user.uid}/${timestamp}_${sanitizedFileName}`;
                const storageRef = ref(storage, filePath);

                // Upload file with progress tracking
                const uploadTask = await uploadBytes(storageRef, file);
                fileUrl = await getDownloadURL(uploadTask.ref);
                fileName = file.name;

                setUploadProgress(100);
            }

            // Prepare work experience data
            const workExpData = {
                userId: user.uid,
                title: formData.title,
                description: formData.description,
                updated_at: serverTimestamp(),
                ...(fileUrl && {
                    fileUrl: fileUrl,
                    original_filename: fileName,
                    fileSize: file.size,
                    fileType: file.type
                })
            };

            if (editMode && experienceData?.id) {
                // Update existing work experience
                const workExpRef = doc(db, 'workExperiences', experienceData.id);
                await updateDoc(workExpRef, workExpData);
                toast.success('Work experience updated successfully!');
            } else {
                // Create new work experience
                workExpData.created_at = serverTimestamp();
                workExpData.status = 'active';
                workExpData.assessment_count = 0;

                await addDoc(collection(db, 'workExperiences'), workExpData);
                toast.success('Work experience added successfully!');
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving work experience:', error);

            // Handle specific Firebase errors
            let errorMessage = 'Failed to save work experience';
            if (error.code === 'storage/unauthorized') {
                errorMessage = 'You do not have permission to upload files';
            } else if (error.code === 'storage/canceled') {
                errorMessage = 'File upload was cancelled';
            } else if (error.code === 'storage/unknown') {
                errorMessage = 'An unknown error occurred during file upload';
            }

            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Validate file size (max 10MB)
            if (selectedFile.size > 10 * 1024 * 1024) {
                toast.error('File size must be less than 10MB');
                return;
            }

            // Validate file type
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(selectedFile.type)) {
                toast.error('Only PDF, DOC, and DOCX files are allowed');
                return;
            }

            setFile(selectedFile);
            setExistingFile(null);
        }
    };

    const removeFile = () => {
        setFile(null);
        setExistingFile(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="work-exp-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl p-8 relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    data-testid="close-work-exp-modal-btn"
                    className="absolute right-4 top-4 text-[#666666] hover:text-[#0A0A0A] transition-colors"
                >
                    <X size={24} />
                </button>

                <h2 className="font-heading text-2xl font-semibold text-[#0A0A0A] mb-6">
                    {editMode ? 'Edit Work Experience' : 'Add Work Experience'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <Label htmlFor="title" className="text-sm font-medium text-[#0A0A0A]">
                            Title <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="title"
                            data-testid="input-title"
                            required
                            placeholder="e.g., Structural Design Engineer at XYZ Consulting"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <Label htmlFor="description" className="text-sm font-medium text-[#0A0A0A]">
                            Description <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                            id="description"
                            data-testid="input-description"
                            required
                            rows={8}
                            placeholder="Describe your work experience, responsibilities, projects, and achievements. Be specific about how your work demonstrates the ECSA outcomes."
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            className="mt-1 rounded-sm border-[#DCDCDA] focus:border-[#002FA7] focus:ring-[#002FA7]/20"
                            disabled={loading}
                        />
                        <p className="text-xs text-[#666666] mt-2">
                            Include details about problem-solving, design work, project management, communication, and ethical considerations.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="file" className="text-sm font-medium text-[#0A0A0A]">
                            Supporting Document (Optional)
                        </Label>
                        <div className="mt-1 border-2 border-dashed border-[#DCDCDA] rounded-sm p-6 text-center hover:border-[#002FA7] transition-colors">
                            <input
                                type="file"
                                id="file"
                                data-testid="input-file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                                className="hidden"
                                disabled={loading}
                            />
                            <label htmlFor="file" className="cursor-pointer block">
                                <Upload size={32} weight="duotone" className="text-[#666666] mx-auto mb-2" />
                                <p className="text-sm text-[#666666] mb-1">
                                    {file ? file.name : existingFile ? existingFile : 'Click to upload or drag and drop'}
                                </p>
                                <p className="text-xs text-[#666666]">PDF, DOC, or DOCX (Max 10MB)</p>
                            </label>

                            {(file || existingFile) && (
                                <button
                                    type="button"
                                    onClick={removeFile}
                                    className="mt-3 text-xs text-red-600 hover:text-red-700"
                                    disabled={loading}
                                >
                                    Remove file
                                </button>
                            )}
                        </div>

                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="mt-2">
                                <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="bg-[#002FA7] h-2 transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                                <p className="text-xs text-[#666666] mt-1">Uploading: {uploadProgress}%</p>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-testid="cancel-btn"
                            className="flex-1 rounded-sm border-[#DCDCDA] hover:bg-gray-50"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-work-exp-btn"
                            className="flex-1 bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update Experience' : 'Add Experience')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WorkExperienceModal;